// @core/stores/notifications/NotificationsSettingStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { notificationsApi } from '@core/api';
import type {
  GetTeamMemberOverridesResponse,
  GetTeamSettingsResponse,
  GetTenantSettingsResponse,
  GetUserSettingsEventResponse,
  GetUserSettingsGlobalResponse,
  UpdateTeamMemberOverridesRequest,
  UpdateTeamSettingsRequest,
  UpdateTenantSettingsRequest,
  UpdateUserSettingsEventRequest,
  UpdateUserSettingsGlobalRequest,
} from '@core/types/notifications';

class NotificationsSettingStore {

  // Loading states
  isLoading = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;

  constructor() {
    makeAutoObservable(this);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }

  reset(): void {
    this.error = null;
  }

  /**
   * Get team member notification overrides
   */
  async GetTeamMemberOverrides(team_id: string, user_id: string): Promise<GetTeamMemberOverridesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await notificationsApi.GetTeamMemberOverrides(team_id, user_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get team notification settings
   */
  async GetTeamSettings(team_id: string): Promise<GetTeamSettingsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await notificationsApi.GetTeamSettings(team_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get tenant notification settings
   */
  async GetTenantSettings(tenant_id: string): Promise<GetTenantSettingsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await notificationsApi.GetTenantSettings(tenant_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get user event-specific notification settings
   */
  async GetUserSettingsEvent(user_id: string): Promise<GetUserSettingsEventResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await notificationsApi.GetUserSettingsEvent(user_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get user global notification settings
   */
  async GetUserSettingsGlobal(user_id: string): Promise<GetUserSettingsGlobalResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await notificationsApi.GetUserSettingsGlobal(user_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update team member notification overrides
   */
  async UpdateTeamMemberOverrides(team_id: string, user_id: string, data: UpdateTeamMemberOverridesRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await notificationsApi.UpdateTeamMemberOverrides(team_id, user_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update team notification settings
   */
  async UpdateTeamSettings(team_id: string, data: UpdateTeamSettingsRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await notificationsApi.UpdateTeamSettings(team_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update tenant notification settings
   */
  async UpdateTenantSettings(tenant_id: string, data: UpdateTenantSettingsRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await notificationsApi.UpdateTenantSettings(tenant_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update user event-specific notification settings
   */
  async UpdateUserSettingsEvent(user_id: string, data: UpdateUserSettingsEventRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await notificationsApi.UpdateUserSettingsEvent(user_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update user global notification settings
   */
  async UpdateUserSettingsGlobal(user_id: string, data: UpdateUserSettingsGlobalRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await notificationsApi.UpdateUserSettingsGlobal(user_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }
}

export const notificationsSettingStore = new NotificationsSettingStore();
